/* eslint-disable @typescript-eslint/camelcase */
import {
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { SyncAlt as SyncAltIcon } from '@material-ui/icons';
import Button from 'components/Button';
import { Formik, FormikErrors } from 'formik';
import i18n from 'i18n';
import { PermissionsData } from 'modules/authenticate/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import { changeAdPasswordRequest } from 'modules/partners/actions';
import { PartnerFormFields } from 'modules/partners/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import { applyI18n } from 'utils/helpers';
import VerificationCode from 'components/VerificationCode';
import { activeDirectoryFields, changeAdPasswordFields } from './formFields';
import { passwordValidationSchema } from './schema';
import styles from './styles';

const useStyles = makeStyles(styles);

const initialValues = {
  new_ad_password: '',
  new_ad_password_confirmation: '',
  action_reason: '',
};

interface ADConfigFieldsProps {
  errors: FormikErrors<PartnerFormFields>;
  isEdit?: boolean;
  formValues: PartnerFormFields;
  partnerValues?: PartnerFormFields | null;
  permissions?: PermissionsData;
  changeAdPasswordRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
}

const ADConfigFields = ({
  errors,
  isEdit,
  formValues,
  partnerValues,
  permissions,
  changeAdPasswordRequest,
  modalClose,
  modalOpen,
  setFieldValue,
}: ADConfigFieldsProps) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const [verificationCode, setVerificationCode] = React.useState<string[]>(
    Array(6).fill('')
  );

  const PASSWORD_MASK = '••••••••••••••••••••••••••';

  const verificationCodeRef = React.useRef(verificationCode);

  React.useEffect(() => {
    verificationCodeRef.current = verificationCode;
  }, [verificationCode]);

  const handleConfirm = values => {
    const verificationCodeToSend = verificationCodeRef.current;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { new_ad_password_confirmation, ...data } = values;

    const updatedData = {
      ...data,
      restricted_action_code: verificationCodeToSend.join(''),
    };

    changeAdPasswordRequest({
      partnerUuid: id,
      values: updatedData,
    });
    modalClose();
  };

  const handleSubmitChangeADPassword = values => {
    setVerificationCode(Array(6).fill(''));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { new_ad_password_confirmation, ...data } = values;
    changeAdPasswordRequest({ partnerUuid: id, values: data });
    modalOpen({
      title: i18n.t('VERIFICATION_CODE_KEY'),
      content: (
        <VerificationCode
          length={6}
          setVerificationCode={setVerificationCode}
          onRetry={() => {
            changeAdPasswordRequest({ partnerUuid: id, values: data });
          }}
          onConfirm={() => {
            handleConfirm(values);
          }}
        />
      ),
      fullWidth: true,
    });
  };

  const openChangeADPasswordModal = () =>
    modalOpen({
      title: i18n.t('PARTNER_PAGE.CHANGE_AD_PASSWORD_KEY'),
      content: (
        <Grid container>
          <Formik
            validationSchema={passwordValidationSchema}
            initialValues={initialValues}
            onSubmit={handleSubmitChangeADPassword}
            validateOnChange
          >
            {({ setFieldValue, errors, isValid, submitForm }) => (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    {`${i18n.t(
                      'PARTNER_PAGE.NEW_AD_PASSWORD_LABEL_FIELD_KEY'
                    )}:`}
                  </Typography>
                </Grid>
                <GenericForm
                  fields={initialValues}
                  screenFields={applyI18n(
                    changeAdPasswordFields.slice(0, 2),
                    i18n.language
                  )}
                  errors={errors}
                  setFieldValue={setFieldValue}
                />

                <Grid item xs={12} style={{ marginTop: 16 }}>
                  <Typography variant="h6">
                    {`${i18n.t(
                      'PARTNER_PAGE.CHANGE_AD_PASSWORD_CONFIRMATION_KEY'
                    )}:`}
                  </Typography>
                </Grid>
                <GenericForm
                  fields={initialValues}
                  screenFields={applyI18n(
                    changeAdPasswordFields.slice(2, 4),
                    i18n.language
                  )}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />

                <Grid
                  container
                  justify="flex-end"
                  className={classes.buttonsBase}
                >
                  <Grid container className={classes.buttonsAlign}>
                    <Button
                      id="submit-button"
                      submitButton
                      onClick={submitForm}
                      disabled={!isValid}
                    >
                      {i18n.t('CONFIRM_KEY')}
                    </Button>
                    <Button
                      id="cancel-button"
                      variant="outlined"
                      onClick={modalClose}
                    >
                      {i18n.t('CANCEL_KEY')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Formik>
        </Grid>
      ),
    });

  return (
    <Grid item container spacing={2}>
      {isEdit && !!partnerValues?.active_directory?.admin_bind_pass ? (
        <>
          <GenericForm
            fields={formValues}
            screenFields={applyI18n(
              activeDirectoryFields.slice(0, 3),
              i18n.language
            )}
            setFieldValue={setFieldValue}
            errors={errors}
          />
          <Grid
            item
            container
            className={classes.adminBindPassField}
            xs={12}
            md={6}
          >
            <GenericForm
              fields={{
                ...formValues,
                active_directory: {
                  ...formValues?.active_directory,
                  admin_bind_pass: partnerValues?.active_directory
                    ?.admin_bind_pass
                    ? PASSWORD_MASK
                    : formValues?.active_directory?.admin_bind_pass ?? '',
                },
              }}
              screenFields={applyI18n(
                [
                  {
                    ...activeDirectoryFields[3],
                    gridXs: permissions?.can_change_external_ad_password
                      ? 10
                      : 12,
                    gridMd: permissions?.can_change_external_ad_password
                      ? 10
                      : 12,
                    disabled: true,
                    isPassword: true,
                    InputProps: {
                      placeholder: partnerValues?.active_directory
                        ?.admin_bind_pass
                        ? '••••••••••••••••••••••••'
                        : '',
                    },
                  },
                ],
                i18n.language
              )}
              setFieldValue={setFieldValue}
              errors={errors}
            />

            {permissions?.can_change_external_ad_password && (
              <Grid item container alignItems="center" justify="center" xs={1}>
                <Tooltip
                  title={i18n.t('PARTNER_PAGE.CHANGE_AD_PASSWORD_KEY')}
                  PopperProps={{ disablePortal: true }}
                >
                  <IconButton
                    className={classes.changePassButton}
                    onClick={openChangeADPasswordModal}
                  >
                    <SyncAltIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Grid>
          <GenericForm
            fields={formValues}
            screenFields={applyI18n(
              activeDirectoryFields.slice(4),
              i18n.language
            )}
            setFieldValue={setFieldValue}
            errors={errors}
          />
        </>
      ) : (
        <GenericForm
          fields={formValues}
          screenFields={applyI18n(activeDirectoryFields, i18n.language)}
          setFieldValue={setFieldValue}
          errors={errors}
        />
      )}
    </Grid>
  );
};

const mapStateToProps = ({ authenticate: { data } }: ApplicationState) => ({
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  changeAdPasswordRequest,
  modalClose,
  modalOpen,
})(ADConfigFields);
